import React, { useState, useEffect, useCallback } from 'react';
import {
  Row, Col, Card, Table, Tag, Typography, Space, Button, Modal, Form,
  Input, Select, InputNumber, Slider, Divider, message, Popconfirm, Tooltip, Alert
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, WifiOutlined,
  SettingOutlined, ApiOutlined, CopyOutlined, ArrowUpOutlined, ArrowDownOutlined
} from '@ant-design/icons';
import {
  getReaders, createReader, updateReader, deleteReader,
  updateReaderStatus, getEvents, getConfLocations
} from '../api';

const { Title, Text } = Typography;
const { Option } = Select;

const TECH       = ['UHF', 'Active RFID', 'NFC', 'BLE'];
const CONN       = ['LAN', 'WiFi', '4G', 'USB'];
const PUSH_TO    = ['Local', 'Cloud', 'Both'];
const STATUS_CLR = { online: 'green', offline: 'red', idle: 'gold' };
const BRANDS     = ['Identium'];

export default function ReaderManager() {
  const [readers,     setReaders]     = useState([]);
  const [events,      setEvents]      = useState([]);
  const [locations,   setLocations]   = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [locLoading,  setLocLoading]  = useState(false);
  const [modalOpen,   setModalOpen]   = useState(false);
  const [editing,     setEditing]     = useState(null);
  const [filterEvent, setFilterEvent] = useState(null);
  const [form] = Form.useForm();

  // Watch form values to build the preview push URL
  const watchedEventId   = Form.useWatch('event_id',        form);
  const watchedLocationId= Form.useWatch('location_id',     form);
  const watchedDirection = Form.useWatch('reader_direction', form);
  const watchedCode      = Form.useWatch('reader_code',      form);
  const ports = Form.useWatch('ports', form) || 4;

  // Build a preview of the push URL (actual generation is on the backend)
  const pushUrlPreview = (() => {
    const evObj   = events.find(e => e.id === watchedEventId);
    const locObj  = locations.find(l => l.id === watchedLocationId);
    const dir     = watchedDirection || 'Entry';
    const code    = watchedCode || 'R1';
    const evCode  = evObj?.event_code   || (watchedEventId  ? '...' : 'EVENT');
    const locCode = locObj?.location_code || (watchedLocationId ? '...' : 'LOCATION');
    return `/api/scan/${encodeURIComponent(evCode)}/${encodeURIComponent(locCode)}/${dir}/${encodeURIComponent(code)}`;
  })();

  const loadReaders = useCallback(() => {
    setLoading(true);
    const params = filterEvent ? { event_id: filterEvent } : {};
    getReaders(params)
      .then(r => setReaders(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filterEvent]);

  const loadLocations = useCallback((eid) => {
    if (!eid) { setLocations([]); return; }
    setLocLoading(true);
    getConfLocations({ event_id: eid })
      .then(r => setLocations(r.data.data || []))
      .catch(() => setLocations([]))
      .finally(() => setLocLoading(false));
  }, []);

  useEffect(() => {
    getEvents().then(r => {
      const evList = r.data.data || [];
      setEvents(evList);
      const active = evList.find(e => e.status === 'active');
      if (active) {
        setFilterEvent(active.id);
        loadLocations(active.id);
      }
    }).catch(() => {});
  }, [loadLocations]);

  useEffect(() => { loadReaders(); }, [loadReaders]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      ports: 4, technology: 'UHF', reader_mode: 'Passive',
      connectivity: 'LAN', push_to: 'Local', status: 'offline',
      reader_direction: 'Entry',
    });
    if (filterEvent) {
      form.setFieldValue('event_id', filterEvent);
      // locations already loaded for filterEvent
    }
    setModalOpen(true);
  };

  const openEdit = (rec) => {
    setEditing(rec);
    const ap = rec.antenna_power
      ? (typeof rec.antenna_power === 'string' ? JSON.parse(rec.antenna_power) : rec.antenna_power)
      : {};
    form.setFieldsValue({
      ...rec,
      reader_direction: rec.reader_direction || 'Entry',
      ...Object.fromEntries(Object.entries(ap).map(([k, v]) => [`ant_${k}`, v]))
    });
    // Load locations for this reader's event
    loadLocations(rec.event_id);
    setModalOpen(true);
  };

  // When event changes in form, reload locations and clear location_id
  const handleFormEventChange = (eid) => {
    form.setFieldValue('location_id', undefined);
    loadLocations(eid);
  };

  const handleSave = async () => {
    try {
      const vals = await form.validateFields();
      const p = vals.ports || 4;
      const antenna_power = {};
      for (let i = 1; i <= p; i++) {
        antenna_power[i] = vals[`ant_${i}`] ?? 27;
        delete vals[`ant_${i}`];
      }
      const payload = { ...vals, antenna_power };
      // push_url is auto-generated by the backend — do not send it
      delete payload.push_url;

      if (editing) {
        await updateReader(editing.id, payload);
        message.success('Reader updated');
      } else {
        await createReader(payload);
        message.success('Reader configured');
      }
      setModalOpen(false);
      loadReaders();
    } catch (e) {
      if (e.errorFields) return; // form validation
      message.error(e.response?.data?.message || 'Save failed');
    }
  };

  const toggleStatus = async (r, newStatus) => {
    try {
      await updateReaderStatus(r.id, { status: newStatus });
      message.success(`Reader set to ${newStatus}`);
      loadReaders();
    } catch { message.error('Status update failed'); }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url).then(() => message.success('URL copied'));
  };

  const columns = [
    { title: '#', width: 45, render: (_, __, i) => i + 1 },
    {
      title: 'Reader', render: (_, r) => (
        <div>
          <Text strong>{r.reader_name}</Text><br />
          <Text code style={{ fontSize: 10 }}>{r.reader_code}</Text>
        </div>
      ),
    },
    { title: 'Brand', dataIndex: 'reader_brand', render: v => v || '-' },
    { title: 'Tech', dataIndex: 'technology', render: v => <Tag color="purple">{v}</Tag>, width: 90 },
    { title: 'Ports', dataIndex: 'ports', width: 55, align: 'center' },
    {
      title: 'Direction', dataIndex: 'reader_direction', width: 90,
      render: v => v === 'Entry'
        ? <Tag icon={<ArrowUpOutlined />} color="green">Entry</Tag>
        : <Tag icon={<ArrowDownOutlined />} color="volcano">Exit</Tag>
    },
    { title: 'Conn.', dataIndex: 'connectivity', render: v => <Tag icon={<WifiOutlined />}>{v}</Tag>, width: 80 },
    { title: 'Push To', dataIndex: 'push_to', render: v => <Tag color="cyan">{v}</Tag>, width: 70 },
    { title: 'IP', dataIndex: 'ip_address', render: v => <Text code style={{ fontSize: 11 }}>{v || '-'}</Text> },
    { title: 'Location', dataIndex: 'location_name', render: v => v || '-' },
    { title: 'Event', dataIndex: 'event_name', render: v => v || '-' },
    {
      title: 'Push URL', dataIndex: 'push_url', width: 260,
      render: (v) => v ? (
        <Space size={4}>
          <Text code style={{ fontSize: 10 }}>{v}</Text>
          <Tooltip title="Copy URL">
            <Button size="small" icon={<CopyOutlined />} onClick={() => copyUrl(v)} />
          </Tooltip>
        </Space>
      ) : <Text type="secondary" style={{ fontSize: 11 }}>Not generated</Text>
    },
    { title: 'Scans/hr', dataIndex: 'scans_last_hour', render: v => v || 0, width: 75, align: 'center' },
    {
      title: 'Status', dataIndex: 'status', width: 95,
      render: (v, r) => (
        <Select value={v} size="small" style={{ width: 90 }} onChange={ns => toggleStatus(r, ns)}>
          {['online', 'offline', 'idle'].map(s => (
            <Option key={s} value={s}>
              <Tag color={STATUS_CLR[s]} style={{ margin: 0 }}>{s}</Tag>
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: 'Actions', width: 80, render: (_, r) => (
        <Space size={4}>
          <Button size="small" icon={<SettingOutlined />} onClick={() => openEdit(r)} />
          <Popconfirm title="Delete this reader?" onConfirm={() =>
            deleteReader(r.id).then(() => { message.success('Deleted'); loadReaders(); })
          }>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row align="middle" justify="space-between" style={{ marginBottom: 20 }}>
        <Col>
          <Title level={4} style={{ margin: 0, color: '#0F1D6B' }}>
            <ApiOutlined style={{ marginRight: 8 }} />Reader Manager
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Configure RFID readers — push URLs are auto-generated per reader
          </Text>
        </Col>
        <Col>
          <Space>
            <Select value={filterEvent} onChange={v => { setFilterEvent(v); loadLocations(v); }}
              style={{ width: 220 }} placeholder="Filter by Event" allowClear>
              {events.map(e => <Option key={e.id} value={e.id}>{e.event_name}</Option>)}
            </Select>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}
              style={{ background: '#0F1D6B' }}>
              Add Reader
            </Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={[12, 8]} style={{ marginBottom: 16 }}>
        {[['online', '#52c41a'], ['offline', '#ff4d4f'], ['idle', '#faad14']].map(([s, c]) => (
          <Col key={s}>
            <Tag color={c} style={{ fontSize: 13, padding: '4px 12px' }}>
              {s.charAt(0).toUpperCase() + s.slice(1)}: {readers.filter(r => r.status === s).length}
            </Tag>
          </Col>
        ))}
        <Col>
          <Tag style={{ fontSize: 13, padding: '4px 12px', background: '#f0f2f5' }}>
            Total: {readers.length}
          </Tag>
        </Col>
      </Row>

      <Card style={{ borderRadius: 10 }}>
        <Table columns={columns} dataSource={readers} rowKey="id" loading={loading}
          size="small" scroll={{ x: 1500 }} pagination={{ pageSize: 15 }} />
      </Card>

      <Modal
        title={editing ? `Edit Reader: ${editing.reader_name}` : 'Configure New Reader'}
        open={modalOpen} onOk={handleSave} onCancel={() => setModalOpen(false)}
        width={800} okButtonProps={{ style: { background: '#0F1D6B' } }}
        destroyOnClose>
        <Form form={form} layout="vertical" size="middle">

          {/* Push URL Preview */}
          <Alert
            type="info"
            style={{ marginBottom: 16 }}
            message={
              <Space direction="vertical" size={2} style={{ width: '100%' }}>
                <Text style={{ fontSize: 12, fontWeight: 600 }}>
                  Auto-Generated Push URL (set this in your reader)
                </Text>
                <Space>
                  <Text code style={{ fontSize: 11, wordBreak: 'break-all' }}>
                    {window.location.origin}{pushUrlPreview}
                  </Text>
                  <Button size="small" icon={<CopyOutlined />}
                    onClick={() => copyUrl(window.location.origin + pushUrlPreview)}>
                    Copy
                  </Button>
                </Space>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  This URL is unique per reader and auto-updates when you change Event / Location / Direction / Reader Code.
                </Text>
              </Space>
            }
          />

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="reader_name" label="Reader Name" rules={[{ required: true }]}>
                <Input placeholder="e.g. Reader-HallA-Entry" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="reader_code" label="Reader Code" rules={[{ required: true }]}>
                <Input placeholder="RDR-001" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="reader_brand" label="Brand">
                <Select placeholder="Select brand" allowClear>
                  {BRANDS.map(b => <Option key={b} value={b}>{b}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="reader_model" label="Model">
                <Input placeholder="Model number" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="technology" label="Technology">
                <Select>{TECH.map(t => <Option key={t} value={t}>{t}</Option>)}</Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="ports" label="No. of Ports">
                <Select>
                  {[1, 2, 4, 8].map(p => <Option key={p} value={p}>{p} Port{p > 1 ? 's' : ''}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="reader_mode" label="Reader Mode">
                <Select>
                  <Option value="Active">Active</Option>
                  <Option value="Passive">Passive</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="connectivity" label="Connectivity">
                <Select>{CONN.map(c => <Option key={c} value={c}>{c}</Option>)}</Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="push_to" label="Push To">
                <Select>{PUSH_TO.map(p => <Option key={p} value={p}>{p}</Option>)}</Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="ip_address" label="Reader IP Address">
                <Input placeholder="192.168.1.101" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="reader_direction"
                label="Reader Direction"
                rules={[{ required: true, message: 'Select direction' }]}>
                <Select>
                  <Option value="Entry">
                    <Space><ArrowUpOutlined style={{ color: '#52c41a' }} />Entry</Space>
                  </Option>
                  <Option value="Exit">
                    <Space><ArrowDownOutlined style={{ color: '#ff4d4f' }} />Exit</Space>
                  </Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              {/* FIX: onChange loads locations and clears location_id */}
              <Form.Item name="event_id" label="Event">
                <Select
                  placeholder="Select event"
                  allowClear
                  onChange={handleFormEventChange}
                >
                  {events.map(e => <Option key={e.id} value={e.id}>{e.event_name}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="location_id" label="Location">
                <Select
                  placeholder={locLoading ? 'Loading...' : 'Select location'}
                  allowClear
                  loading={locLoading}
                  disabled={!watchedEventId}
                  notFoundContent={watchedEventId ? 'No locations found' : 'Select an event first'}
                >
                  {locations.map(l => (
                    <Option key={l.id} value={l.id}>
                      {l.location_name}
                      <Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>
                        ({l.location_code})
                      </Text>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" style={{ fontSize: 12, color: '#888' }}>
            Antenna Power (dBm)
          </Divider>
          <Row gutter={[16, 0]}>
            {Array.from({ length: ports }, (_, i) => i + 1).map(p => (
              <Col span={12} key={p}>
                <Form.Item name={`ant_${p}`} label={`Antenna ${p}`} initialValue={27}>
                  <Row gutter={8} align="middle">
                    <Col flex="1">
                      <Slider min={5} max={33} defaultValue={27} />
                    </Col>
                    <Col>
                      <InputNumber min={5} max={33} style={{ width: 60 }} />
                    </Col>
                  </Row>
                </Form.Item>
              </Col>
            ))}
          </Row>

          <Form.Item name="status" label="Initial Status">
            <Select>
              {['online', 'offline', 'idle'].map(s => (
                <Option key={s} value={s}>
                  <Tag color={STATUS_CLR[s]}>{s}</Tag>
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
